/** Helpers for KBO chat empty/unnamed thread hygiene (KFO consumer). */

export const DEFAULT_CHAT_THREAD_NAME = 'Krybot Chat';

export interface ThreadHygieneFields {
  id: string;
  name?: string | null;
  messageCount?: number;
  isEmpty?: boolean;
  isDefaultNamed?: boolean;
}

/** Backend contract: empty + default/blank name = reusable draft. */
export function isReusableEmptyDraft(thread: ThreadHygieneFields): boolean {
  const empty =
    thread.isEmpty === true ||
    (typeof thread.messageCount === 'number' && thread.messageCount === 0);
  if (!empty) return false;
  return isDefaultNamedThread(thread);
}

export function isDefaultNamedThread(thread: ThreadHygieneFields): boolean {
  if (thread.isDefaultNamed === true) return true;
  const name = (thread.name ?? '').trim();
  return name.length === 0 || name === DEFAULT_CHAT_THREAD_NAME;
}

/** Newest reusable draft first (caller should pass already sorted by updatedAt desc). */
export function findReusableEmptyDraft<T extends ThreadHygieneFields>(
  threads: T[]
): T | undefined {
  return threads.find((t) => isReusableEmptyDraft(t));
}

export function upsertThreadById<T extends { id: string }>(threads: T[], thread: T): T[] {
  const idx = threads.findIndex((t) => t.id === thread.id);
  if (idx < 0) return [thread, ...threads];
  const next = threads.slice();
  next[idx] = { ...next[idx], ...thread };
  return next;
}

/**
 * When bootstrap reuses a draft, keep the server title unless the caller is
 * intentionally renaming after first contact (handled via updateThread).
 */
export function resolveBootstrapThreadName(
  serverName: string | undefined,
  requestedName: string | undefined,
  threadReused: boolean
): string {
  if (threadReused) {
    const server = (serverName ?? '').trim();
    return server || DEFAULT_CHAT_THREAD_NAME;
  }
  const requested = (requestedName ?? '').trim();
  if (requested) return requested;
  const server = (serverName ?? '').trim();
  return server || DEFAULT_CHAT_THREAD_NAME;
}

export function formatThreadListLabel(thread: ThreadHygieneFields): string {
  const base = (thread.name ?? '').trim() || DEFAULT_CHAT_THREAD_NAME;
  const empty =
    thread.isEmpty === true ||
    (typeof thread.messageCount === 'number' && thread.messageCount === 0);
  if (empty && isDefaultNamedThread(thread)) return `${base} · empty draft`;
  if (empty) return `${base} · empty`;
  if (isDefaultNamedThread(thread)) return `${base} · untitled`;
  return base;
}

/** Prefer non-empty threads, then named, then newest. */
export function sortThreadsForListDisplay<T extends ThreadHygieneFields & { updatedAt?: string }>(
  threads: T[]
): T[] {
  return [...threads].sort((a, b) => {
    const aEmpty = a.isEmpty === true || a.messageCount === 0;
    const bEmpty = b.isEmpty === true || b.messageCount === 0;
    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
    const aDefault = isDefaultNamedThread(a);
    const bDefault = isDefaultNamedThread(b);
    if (aDefault !== bDefault) return aDefault ? 1 : -1;
    const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return bTime - aTime;
  });
}
